import ContextualBar from '@/components/ContextualBar';
import CriticalBanner from '@/components/CriticalBanner';
import CustomCard from '@/components/customCard';
import { mapTypeToTitle } from '@/components/ExcludeRuleCondition';
import GridLayoutPreview from '@/components/GridLayoutPreview';
import Layout from '@/components/layout';
import ModalConfirmLeavePage from '@/components/ModalConfirmLeavePage';
import PreviewCheckout from '@/components/Preview/PreviewCheckout';
import RuleSummary from '@/components/RuleSummary';
import { stickyTopCreateCheckoutRule } from '@/constants';
import {
  ItemConditionEnum,
  LocationConditionEnum,
  PrefixIconColorKeys,
  PrefixIcon as PrefixIconEnum,
  PrefixIconSizeKeys,
  TextAppearanceKeys,
  TextEmphasisKeys,
  TextSizeKeys,
} from '@/constants/enum';
import { PATH } from '@/constants/path';
import { checkShowErrorInline } from '@/helpers';
import { useCustomUserIdClarity } from '@/hooks/useCustomUserIdClarity';
import useHandleContextual from '@/hooks/useHandleContextureBar';
import useShippingMethodSeparator from '@/hooks/useShippingMethodSeparator';
import useToast from '@/hooks/useToast';
import useUserPlan from '@/hooks/useUserPlan';
import Step1 from '@/pages/Checkout/components/Step1';
import Step2 from '@/pages/Checkout/components/Step2';
import Step3 from '@/pages/Checkout/components/Step3';
import { CURuleStyled } from '@/pages/Settings/Components/EstimateRules/components/CURule/styled';
import { apiCaller } from '@/redux/query';
import appbarSlice, { clickedDiscardSelector, isShowContextualBarSelector } from '@/redux/slice/appbar.slice';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import checkoutRuleSlice, {
  checkoutRuleErrorsSelector,
  checkoutRuleSettingsSelector,
  initCheckoutRuleState,
} from '@/redux/slice/checkoutRule.slice';
import { IOptions } from '@/types';
import { IItemCondition, ILocationCondition, IRule, RelationCondition } from '@/types/newSetup.type';
import { Divider, Tabs, TabsProps } from '@shopify/polaris';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

export interface IStateCheckoutRule {
  id?: number;
  name: string;
  shippingMethods: IOptions[];
  enable: boolean;
  messageText: string;
  minPreparationDays: number;
  maxPreparationDays: number;
  minDeliveryDays: number;
  maxDeliveryDays: number;
  iconId: PrefixIconEnum;
  textSize: TextSizeKeys;
  textAppearance: TextAppearanceKeys;
  textEmphasis: TextEmphasisKeys;
  iconSize: PrefixIconSizeKeys;
  iconColor: PrefixIconColorKeys;
  updatedAt?: string;
  createdAt?: string;
  checkoutItemConditions: IItemCondition[];
  checkoutLocationConditions: ILocationCondition[];
  typeRelationOfItemCondition: RelationCondition;
}

export type IState = IRule;

const tabs: TabsProps['tabs'] = [
  {
    id: 'step-1',
    content: (<p className="fw-500">Estimate</p>) as any,
  },
  {
    id: 'step-2',
    content: <p className="fw-500">Display conditions</p>,
  },
  {
    id: 'extra',
    content: <p className="fw-500">Appearance</p>,
  },
];

export default function CURule() {
  const shippingMethodSeparator = useShippingMethodSeparator();
  const { isEnableCheckout } = useUserPlan();
  const clickedDiscard = useSelector(clickedDiscardSelector);
  const [currentStep, setCurrentStep] = useState(0);
  useCustomUserIdClarity('CU checkout rule');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { show } = useToast();
  const contextualBar = useHandleContextual();
  const isSkip = useSelector(isSkipApiSelector);
  const isShowContextualBar = useSelector(isShowContextualBarSelector);
  const [create, { isLoading: isLoadingCreate }] = apiCaller.useCreateCheckoutRuleMutation();
  const [update, { isLoading: isLoadingUpdate }] = apiCaller.useUpdateCheckoutRuleMutation();
  const [openModalConfirmLeave, setOpenModalConfirmLeave] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const { id } = useParams();
  const { data: dataRuleRes, isLoading } = apiCaller.useGetDetailCheckoutRuleQuery(id ? Number(id) : -1, {
    skip: !id || isSkip,
    refetchOnMountOrArgChange: true,
  });

  const state = useSelector(checkoutRuleSettingsSelector);
  const errors = useSelector(checkoutRuleErrorsSelector);

  const content = useMemo(() => {
    switch (currentStep) {
      case 0:
        return <Step1 />;
      case 1:
        return <Step2 />;
      default:
        return <Step3 />;
    }
  }, [currentStep]);

  useEffect(() => {
    if (isEnableCheckout) return;
    navigate(PATH.CHECKOUT_EXTENSION);
  }, [isEnableCheckout, navigate]);

  useEffect(() => {
    if (clickedDiscard) {
      dispatch(checkoutRuleSlice.actions.clearError());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickedDiscard]);

  const errorBanner = useMemo(() => {
    const serverError = errors.find((error) => error.field === 'serverError');
    if (!serverError) return null;

    return (
      <div className="wrap-banner-error">
        <CustomCard>
          <CriticalBanner isVisible={true}>{serverError.msg}</CriticalBanner>
        </CustomCard>
      </div>
    );
  }, [errors]);

  const currentOrderValue = useMemo(() => {
    let max = 0;
    dataRuleRes?.data?.checkoutItemConditions?.forEach((item) => {
      if (item?.order && item.order > max) max = item.order;
    });

    dataRuleRes?.data?.checkoutLocationConditions?.forEach((item) => {
      if (item?.order && item.order > max) max = item.order;
    });
    return max;
  }, [dataRuleRes?.data]);

  const handleInitState = useCallback(() => {
    if (id && dataRuleRes?.data) {
      const shippingMethods: IOptions[] = dataRuleRes?.data.shippingMethods
        ?.split(shippingMethodSeparator)
        ?.map((item) => ({ value: item, label: item }));
      dispatch(
        checkoutRuleSlice.actions.setValues({
          value: { ...dataRuleRes?.data, shippingMethods },
          currentCount: currentOrderValue,
        }),
      );
    } else {
      dispatch(checkoutRuleSlice.actions.setValues({ value: initCheckoutRuleState, currentCount: 0 }));
    }
  }, [id, dataRuleRes?.data, shippingMethodSeparator, dispatch, currentOrderValue]);

  useEffect(() => {
    dispatch(checkoutRuleSlice.actions.clearError());
    handleInitState();
  }, [dispatch, handleInitState]);

  useEffect(() => {
    if (isLoading) return;
    let temp = false;
    if (!id) temp = !isEqual(state, initCheckoutRuleState);
    else {
      const dataRule = {
        ...dataRuleRes?.data,
        shippingMethods: dataRuleRes?.data?.shippingMethods
          ?.split(shippingMethodSeparator)
          ?.map((item) => ({ value: item, label: item })),
      };
      temp = !isEqual(state, dataRule);
    }
    dispatch(appbarSlice.actions.handleUpdateContextualBarState({ isShowContextualBar: temp }));
  }, [dataRuleRes?.data, id, state, isSaved, isLoading, dispatch, shippingMethodSeparator]);

  const isDisabledDiscard = useMemo(() => {
    let check = true;
    if (id) {
      check = !isShowContextualBar;
    } else {
      check = isEqual(state, initCheckoutRuleState);
    }
    return check;
  }, [id, isShowContextualBar, state]);

  const isDisableSave = useMemo(() => {
    const check = !state.shippingMethods.length || !state?.name?.length || !state.name?.length || errors.length > 0;
    return check;
  }, [state.shippingMethods.length, state?.name?.length, errors.length]);

  const handleSave = () => {
    if (isSkip) return;
    const newShippingMethods = state.shippingMethods.map((item) => item.value);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { createdAt, updatedAt, ...rest } = state || {};
    const dataSubmit = {
      ...rest,
      shippingMethods: newShippingMethods,
      id: Number(id) || undefined,
      minPreparationDays: +state.minPreparationDays,
      maxPreparationDays: +state.maxPreparationDays,
      minDeliveryDays: +state.minDeliveryDays,
      maxDeliveryDays: +state.maxDeliveryDays,
      checkoutItemConditions: state.checkoutItemConditions?.map((i) => ({
        ...i,
        value: i.value?.filter((el) => el.data),
      })),
      checkoutLocationConditions: state.checkoutLocationConditions?.map((i) => ({
        ...i,
        value: i.value?.filter((el) => el.data),
      })),
      typeRelationOfItemCondition: state.typeRelationOfItemCondition,
    };
    const mutation = id ? update : create;
    //@ts-ignore-next-line
    mutation(dataSubmit).then((res) => {
      const checkError = checkShowErrorInline(res);
      if (checkError.status) {
        window.scrollTo(0, 0);
        dispatch(checkoutRuleSlice.actions.handleSetError({ value: { field: 'serverError', msg: checkError.msg } }));
      } else {
        setIsSaved(true);
        show(`Rule ${id ? 'updated' : 'created'}`);
        navigate(PATH.CHECKOUT_EXTENSION);
      }
    });
  };

  const handleDiscard = () => {
    handleInitState();
  };

  const handleStatus = () => {
    if (!state) return;
    dispatch(checkoutRuleSlice.actions.handleChange({ key: 'enable', value: !state.enable }));
  };

  const isEmptyCondition = useMemo(() => {
    return state?.checkoutItemConditions?.length === 0 && state?.checkoutLocationConditions?.length === 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(state)]);

  const locationConditions = useMemo(() => {
    const mapTitle = {
      [LocationConditionEnum.Country]: 'Locations, regions',
      [LocationConditionEnum.State]: 'States',
      [LocationConditionEnum.Zipcode]: 'Zipcodes',
      [LocationConditionEnum.City]: 'Cities',
    };
    return (
      state?.checkoutLocationConditions?.map(
        (item) =>
          `${mapTitle[item.type]} (${
            item.type !== LocationConditionEnum.Zipcode && item.type !== LocationConditionEnum.City
              ? item.value?.filter((i) => i?.data)?.length
              : item.value?.[0]?.data?.split(',')?.filter((i) => !!i)?.length
          })`,
      ) || []
    );
  }, [state?.checkoutLocationConditions]);

  const itemConditions = useMemo(() => {
    const inventoryData = state?.checkoutItemConditions?.find((item) => item.type === ItemConditionEnum.InventoryQuantity)
      ?.value?.[0];
    const inventoryText = `${inventoryData?.minInventoryQuantity ? 'Minimum: ' : ''}${
      inventoryData?.minInventoryQuantity ? inventoryData?.minInventoryQuantity : ''
    } ${inventoryData?.minInventoryQuantity && inventoryData?.maxInventoryQuantity ? '-' : ''} ${
      inventoryData?.maxInventoryQuantity ? 'Maximum: ' : ''
    }${inventoryData?.maxInventoryQuantity ? inventoryData?.maxInventoryQuantity : ''}`;

    return (
      state?.checkoutItemConditions
        ?.filter((item) => item.type !== ItemConditionEnum.Exception)
        ?.map(
          (item) =>
            `${mapTypeToTitle[item.type]} (${
              item.type !== ItemConditionEnum.InventoryQuantity ? item.value?.filter((i) => i?.data)?.length : inventoryText
            })`,
        ) || []
    );
  }, [state?.checkoutItemConditions]);

  const textDataException: React.ReactNode = useMemo(() => {
    const dataException = state?.checkoutItemConditions?.find((item) => item.type === ItemConditionEnum.Exception)?.value;
    return dataException ? (
      <>
        <Divider />
        <div className="mt-4">{`${mapTypeToTitle[ItemConditionEnum.Exception]} (${
          dataException?.filter((i) => i?.data)?.length
        })`}</div>
      </>
    ) : null;
  }, [state?.checkoutItemConditions]);

  const handleClickBackAction = useCallback(() => {
    dispatch(appbarSlice.actions.handleUpdateContextualBarState({ isShowContextualBar: false }));
    navigate(PATH.CHECKOUT_EXTENSION);
    contextualBar?.hide();
  }, [contextualBar, dispatch, navigate]);

  return (
    <CURuleStyled>
      <Layout
        title={`${id ? 'Edit' : 'New'} checkout shipping method rule`}
        backAction={{
          onAction: () => {
            isShowContextualBar ? setOpenModalConfirmLeave(true) : handleClickBackAction();
          },
        }}
        headers={
          <ContextualBar
            isVisible={isShowContextualBar}
            saveAction={handleSave}
            loadingSave={isLoadingCreate || isLoadingUpdate}
            isDisabled={isDisableSave}
            isDisabledDiscard={isDisabledDiscard}
            discardAction={handleDiscard}
          />
        }
      >
        <div>
          {errorBanner}
          <div className="mb-16 tabs w-fit">
            <Tabs
              tabs={tabs}
              selected={currentStep}
              onSelect={(selectedTabIndex) => {
                setCurrentStep(selectedTabIndex);
              }}
              fitted
            ></Tabs>
          </div>
        </div>
        <GridLayoutPreview>
          <div>{content}</div>
          <div
            className="d-flex flex-column gap-10"
            style={{ height: 'fit-content', position: 'sticky', top: stickyTopCreateCheckoutRule }}
          >
            <RuleSummary
              defaultData={state}
              isDisableActive={false}
              handleStatus={handleStatus}
              isEmptyCondition={isEmptyCondition}
              locationConditions={locationConditions}
              itemConditions={itemConditions}
              textDataException={textDataException}
              isInCheckout
            />
            <PreviewCheckout top={stickyTopCreateCheckoutRule} dataCheckout={state} />
          </div>
        </GridLayoutPreview>
      </Layout>
      <ModalConfirmLeavePage
        open={openModalConfirmLeave}
        onClose={() => setOpenModalConfirmLeave(false)}
        onConfirm={handleClickBackAction}
      />
    </CURuleStyled>
  );
}
